<template>
  <div class="onboarding-stage-form">
    <div class="progress-header">
      <div class="flex items-center justify-end">
        <span class="text-xs text-gray-500">Progress</span>
        <div class="w-32 bg-gray-200 rounded-full h-2 mx-2">
          <div
            class="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
            :style="`width: ${progressPercentage}%`" />
        </div>
        <span class="text-xs font-medium text-gray-700">{{ progressPercentage }}%</span>
      </div>
    </div>

    <div
      v-if="loading"
      class="content-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-blue-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading onboarding stage...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="content-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div class="space-y-4">
          <!-- Section 1: Call Recording & Coordinator -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-headset text-gray-500 mr-2" />
              Onboarding Call
            </h6>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-microphone mr-1.5" />
                  Onboarding Call Recording
                </div>
                <div v-if="onboardingStage.onboardingCallRecordingUrl">
                  <a
                    :href="onboardingStage.onboardingCallRecordingUrl"
                    target="_blank"
                    class="info-link">
                    <i class="fas fa-play-circle mr-1.5" />
                    Listen to Recording
                    <i class="fas fa-external-link-alt ml-1 text-xs" />
                  </a>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not recorded
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-user-tie mr-1.5" />
                  Onboarding Coordinator
                </div>
                <div class="info-value">
                  {{ onboardingStage.onboardingCoordinator?.displayName || 'Not assigned' }}
                </div>
              </div>
            </div>

            <div
              v-if="onboardingStage.onboardingTranscript"
              class="mt-3">
              <div class="info-label mb-2">
                <i class="fas fa-file-alt mr-1.5" />
                Call Transcript
              </div>
              <div class="transcript-box">
                {{ onboardingStage.onboardingTranscript }}
              </div>
            </div>
          </div>

          <!-- Section 2: Setup Checklist -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-clipboard-check text-gray-500 mr-2" />
              Setup Checklist
            </h6>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    onboardingStage.clientDashboardConnected ? 'fa-check-square text-blue-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="onboardingStage.clientDashboardConnected ? 'text-gray-700' : 'text-gray-500'">
                  Client Dashboard Connected
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    onboardingStage.lindaAppInstalled ? 'fa-check-square text-blue-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="onboardingStage.lindaAppInstalled ? 'text-gray-700' : 'text-gray-500'">
                  Linda App Installed & Active
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    onboardingStage.serviceCheckinsEnabled ? 'fa-check-square text-blue-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="onboardingStage.serviceCheckinsEnabled ? 'text-gray-700' : 'text-gray-500'">
                  Service Check-ins Enabled
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    onboardingStage.reviewRequestSetup ? 'fa-check-square text-blue-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="onboardingStage.reviewRequestSetup ? 'text-gray-700' : 'text-gray-500'">
                  Review Request System Setup
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    onboardingStage.performanceCallScheduled ? 'fa-check-square text-blue-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="onboardingStage.performanceCallScheduled ? 'text-gray-700' : 'text-gray-500'">
                  Performance Call Scheduled
                </span>
              </div>
            </div>
          </div>

          <!-- Section 3: Keywords & Notes -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-search text-gray-500 mr-2" />
              Keywords & Notes
            </h6>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="info-item">
                <div class="info-label mb-2">
                  <i class="fas fa-map-marker-alt mr-1.5" />
                  Geogrid Keywords Tracked
                </div>
                <div v-if="onboardingStage.geogridKeywordsArray && onboardingStage.geogridKeywordsArray.length">
                  <div class="keywords-box">
                    <div
                      v-for="(keyword, index) in onboardingStage.geogridKeywordsArray"
                      :key="index"
                      class="keyword-tag">
                      {{ keyword }}
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  No keywords tracked
                </div>
              </div>

              <div class="info-item">
                <div class="info-label mb-2">
                  <i class="fas fa-sticky-note mr-1.5" />
                  Onboarding Notes
                </div>
                <div v-if="onboardingStage.onboardingNotes">
                  <div class="notes-box">
                    {{ onboardingStage.onboardingNotes }}
                  </div>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  No notes added
                </div>
              </div>
            </div>
          </div>

          <!-- Section 4: Email Status -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-envelope text-gray-500 mr-2" />
              Communication Status
            </h6>
            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-paper-plane mr-1.5" />
                Coordinator Email Status
              </div>
              <div v-if="onboardingStage.coordinatorEmailSentAt">
                <span class="text-sm text-blue-600 font-medium">
                  <i class="fas fa-check-circle mr-1" />
                  Sent to coordinator on {{ formatDateTime(onboardingStage.coordinatorEmailSentAt) }}
                </span>
              </div>
              <div
                v-else
                class="text-sm text-amber-600">
                <i class="fas fa-clock mr-1" />
                Not sent yet
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-between">
          <button
            v-if="onboardingStage.onboardingCoordinator && !onboardingStage.coordinatorEmailSentAt"
            class="action-button action-button-secondary"
            :disabled="sendingEmail"
            @click="sendCoordinatorEmail">
            <i
              v-if="sendingEmail"
              class="fas fa-spinner fa-spin mr-1.5" />
            <i
              v-else
              class="fas fa-envelope mr-1.5" />
            Send to Performance Manager
          </button>
          <div v-else />
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-edit mr-1.5" />
            Edit Onboarding Stage
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-6"
        @submit.prevent="saveOnboardingStage">
        <!-- Section 1: Onboarding Call -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-headset text-gray-500 mr-2" />
            Onboarding Call
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Onboarding Call Recording URL</label>
              <input
                v-model="form.onboarding_call_recording_url"
                type="url"
                class="form-input"
                placeholder="https://example.com/recording">
            </div>
            <div class="form-group">
              <label class="form-label">Onboarding Coordinator</label>
              <select
                v-model="form.onboarding_coordinator_id"
                class="form-input">
                <option value="">
                  Select coordinator
                </option>
                <option
                  v-for="member in teamMembers"
                  :key="member.id"
                  :value="member.id">
                  {{ member.displayName }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Call Transcript</label>
            <textarea
              v-model="form.onboarding_transcript"
              rows="4"
              class="form-input"
              placeholder="Enter call transcript or key notes..." />
          </div>
        </div>

        <!-- Section 2: Setup Checklist -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-clipboard-check text-gray-500 mr-2" />
            Setup Checklist
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label class="checklist-label">
              <input
                v-model="form.client_dashboard_connected"
                type="checkbox"
                class="mr-2 text-blue-600 rounded">
              Client Dashboard Connected
            </label>
            <label class="checklist-label">
              <input
                v-model="form.linda_app_installed"
                type="checkbox"
                class="mr-2 text-blue-600 rounded">
              Linda App Installed & Active
            </label>
            <label class="checklist-label">
              <input
                v-model="form.service_checkins_enabled"
                type="checkbox"
                class="mr-2 text-blue-600 rounded">
              Service Check-ins Enabled
            </label>
            <label class="checklist-label">
              <input
                v-model="form.review_request_setup"
                type="checkbox"
                class="mr-2 text-blue-600 rounded">
              Review Request System Setup
            </label>
            <label class="checklist-label">
              <input
                v-model="form.performance_call_scheduled"
                type="checkbox"
                class="mr-2 text-blue-600 rounded">
              Performance Call Scheduled
            </label>
          </div>
        </div>

        <!-- Section 3: Keywords & Notes -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-search text-gray-500 mr-2" />
            Keywords & Notes
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Geogrid Keywords Tracked</label>
              <textarea
                v-model="form.geogrid_keywords"
                rows="6"
                class="form-input"
                placeholder="Enter keywords, one per line..." />
              <p class="text-xs text-gray-500 mt-1">
                Enter each keyword on a new line
              </p>
            </div>
            <div class="form-group">
              <label class="form-label">Onboarding Notes</label>
              <textarea
                v-model="form.onboarding_notes"
                rows="6"
                class="form-input"
                placeholder="Important notes for the performance manager..." />
            </div>
          </div>
        </div>

        <div class="form-actions-sticky">
          <div class="form-actions">
            <div class="flex gap-2">
              <button
                type="submit"
                :disabled="saving"
                class="action-button action-button-success">
                <i
                  v-if="saving"
                  class="fas fa-spinner fa-spin mr-1.5" />
                <i
                  v-else
                  class="fas fa-save mr-1.5" />
                Save
              </button>
              <button
                type="button"
                :disabled="saving"
                class="action-button action-button-secondary"
                @click="cancelEditing">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const sendingEmail = ref(false)
const isEditing = ref(false)
const onboardingStage = ref({})
const teamMembers = ref([])
const form = ref({
  onboarding_call_recording_url: '',
  onboarding_transcript: '',
  onboarding_coordinator_id: '',
  client_dashboard_connected: false,
  linda_app_installed: false,
  service_checkins_enabled: false,
  review_request_setup: false,
  geogrid_keywords: '',
  performance_call_scheduled: false,
  onboarding_notes: ''
})

// Computed
const progressPercentage = computed(() => {
  if (!onboardingStage.value) return 0
  return onboardingStage.value.progressPercentage || 0
})

// Methods
const fetchOnboardingStage = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/onboarding_stage`)
    if (response.data.success) {
      onboardingStage.value = response.data.onboardingStage || {}
      teamMembers.value = response.data.teamMembers || []
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching onboarding stage:', error)
    mdtoast.error('Failed to load onboarding stage', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const startEditing = () => {
  const stage = onboardingStage.value
  form.value = {
    onboarding_call_recording_url: stage.onboardingCallRecordingUrl || '',
    onboarding_transcript: stage.onboardingTranscript || '',
    onboarding_coordinator_id: stage.onboardingCoordinatorId || '',
    client_dashboard_connected: stage.clientDashboardConnected || false,
    linda_app_installed: stage.lindaAppInstalled || false,
    service_checkins_enabled: stage.serviceCheckinsEnabled || false,
    review_request_setup: stage.reviewRequestSetup || false,
    geogrid_keywords: stage.geogridKeywords || '',
    performance_call_scheduled: stage.performanceCallScheduled || false,
    onboarding_notes: stage.onboardingNotes || ''
  }
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
}

const saveOnboardingStage = async() => {
  try {
    saving.value = true
    const payload = { onboarding_stage: form.value }
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/onboarding_stage`,
      payload
    )

    if (response.data.success) {
      onboardingStage.value = response.data.onboardingStage
      isEditing.value = false
      mdtoast.success('Onboarding stage saved successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to save onboarding stage', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving onboarding stage:', error)
    mdtoast.error('Failed to save onboarding stage', { duration: 3000 })
  } finally {
    saving.value = false
  }
}

const sendCoordinatorEmail = async() => {
  try {
    sendingEmail.value = true
    const response = await axios.post(`/ajax/organizations/${props.organizationId}/onboarding_stage/send_coordinator_email`)

    if (response.data.success) {
      mdtoast.success('Email sent successfully to coordinator', { duration: 3000 })
      // Refresh data to show updated email sent status
      await fetchOnboardingStage()
    } else {
      mdtoast.error(response.data.error || 'Failed to send email', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error sending coordinator email:', error)
    mdtoast.error('Failed to send email to coordinator', { duration: 3000 })
  } finally {
    sendingEmail.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchOnboardingStage()
})
</script>

<style scoped>
.onboarding-stage-form {
  display: flex;
  flex-direction: column;
}

.progress-header {
  @apply mb-4;
}

.content-body {
  overflow-y: auto;
  flex: 1;
}

.section {
  @apply bg-gray-50 rounded-lg p-4 space-y-3;
}

.section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.info-item {
  @apply space-y-1;
}

.info-label {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center;
}

.info-value {
  @apply text-sm text-gray-900;
}

.info-link {
  @apply text-sm text-indigo-600 hover:text-indigo-800 transition-colors
         inline-flex items-center hover:underline;
}

.transcript-box {
  @apply bg-white rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-200;
}

.notes-box {
  @apply bg-white rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-200;
}

.keywords-box {
  @apply flex flex-wrap gap-2;
}

.keyword-tag {
  @apply bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md;
}

.checklist-item {
  @apply flex items-center text-sm;
}

.form-section {
  @apply bg-gray-50 rounded-lg p-4 space-y-4;
}

.form-section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
         transition-colors duration-200;
}

.checklist-label {
  @apply flex items-center text-sm text-gray-700 cursor-pointer hover:text-gray-900;
}

.form-actions-sticky {
  position: sticky;
  bottom: 0;
  background-color: white;
  margin-top: 1.5rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  margin-bottom: -1.25rem;
  padding: 0 1.25rem;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.form-actions {
  @apply flex items-center justify-between py-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}
</style>
